import React from "react";
import { Link } from "react-router-dom";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { Skeleton } from "../../../shares/dashboard/DashboardWidgets";

/**
 * Who the student is + when their class runs.
 *
 * group_day / group_time from dashboard/userinfo-box are the weekday and hour
 * of the student's own weekly class - the old header showed them as a labelled
 * pair and they are read that way here too. Matching group_day against today's
 * Persian weekday only adds an "it's today" highlight on top; it never replaces
 * the values themselves.
 */
function ScheduleBlock({ schedule }) {
    const day = schedule?.day;
    const time = schedule?.time;
    const hasSchedule = Boolean(day || time);

    // Deliberately never returns null. This block vanishing when the API sends
    // nothing is indistinguishable from the block not being built at all, which
    // is exactly how the first version of this hid a data problem.
    const Icon = !hasSchedule
        ? HelpOutlineIcon
        : schedule.isToday
        ? EventAvailableIcon
        : ScheduleIcon;

    if (!hasSchedule) {
        return (
            <div className="flex items-center gap-md rounded-card border border-dashed border-rule bg-surface px-lg py-md">
                <span className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-soft bg-surface-sunken text-ink-subtle">
                    <Icon sx={{ fontSize: "2rem" }} />
                </span>
                <div className="min-w-0">
                    <p className="text-xs text-ink-subtle">کلاس هفتگی تو</p>
                    <p className="mt-2xs text-base text-ink-muted">
                        هنوز ثبت نشده
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-md rounded-card border border-rule-subtle bg-surface px-lg py-md">
            <span
                className={`flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-soft ${
                    schedule.isToday
                        ? "bg-success-soft text-success"
                        : "bg-primary-soft text-primary"
                }`}
            >
                <Icon sx={{ fontSize: "2rem" }} />
            </span>

            <div className="min-w-0">
                <p className="text-xs text-ink-subtle">
                    {schedule.isToday ? "کلاس تو، امروز" : "کلاس هفتگی تو"}
                </p>
                <p className="mt-2xs flex flex-wrap items-baseline gap-x-sm text-base font-medium text-ink">
                    {day && <span>{day}</span>}
                    {day && time && (
                        <span aria-hidden="true" className="text-rule">
                            ·
                        </span>
                    )}
                    {time && <span className="tabular-nums">ساعت {time}</span>}
                </p>
            </div>
        </div>
    );
}

export default function WelcomeHero({ student, schedule, loading }) {
    return (
        <section className="dash-hero dash-rise rounded-panel border border-rule-subtle p-2xl shadow-card max-md:p-xl">
            <div className="relative z-[1] flex flex-wrap items-end justify-between gap-lg">
                <div className="min-w-0">
                    {loading && !student?.fullName ? (
                        <div className="flex flex-col gap-sm">
                            <Skeleton className="h-[1.6rem] w-[12rem]" />
                            <Skeleton className="h-[3.2rem] w-[24rem]" />
                        </div>
                    ) : (
                        <>
                            <p className="text-base text-ink-subtle">سلام</p>
                            <h1 className="mt-2xs text-4xl font-semibold text-ink max-md:text-3xl">
                                {student?.firstName || "دانش‌آموز"} عزیز
                            </h1>
                        </>
                    )}

                    <p className="mt-sm max-w-[46ch] text-base text-ink-muted">
                        هرچی امروز لازم داری همین‌جاست. یه نگاه بنداز و ادامه بده.
                    </p>

                    {student?.group && (
                        <span className="mt-lg inline-flex items-center rounded-pill bg-primary-soft px-md py-xs text-sm text-primary">
                            {student.group}
                        </span>
                    )}
                </div>

                <div className="flex shrink-0 flex-col items-stretch gap-md max-md:w-full">
                    {loading && !schedule?.day ? (
                        <Skeleton className="h-[7rem] w-[24rem] max-md:w-full" />
                    ) : (
                        <ScheduleBlock schedule={schedule} />
                    )}

                    <Link
                        to="/dashboard/classes"
                        className="inline-flex items-center justify-center gap-xs rounded-soft bg-primary px-xl py-sm text-base text-white shadow-card transition-colors duration-150 hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    >
                        <PlayCircleOutlineIcon sx={{ fontSize: "1.8rem" }} />
                        ادامه جلسات
                    </Link>
                </div>
            </div>
        </section>
    );
}
